import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { AuthSliceInitialState } from '@/domains/auth/store/types/AuthSliceInitialState';
import type { ForgotPasswordRequest } from '@/domains/auth/types/api/requests/ForgotPasswordRequest';
import type { ForgotUsernameRequest } from '@/domains/auth/types/api/requests/ForgotUsernameRequest';
import type { OtpSignInRequest } from '@/domains/auth/types/api/requests/OtpSignInRequest';
import type { SignInRequest } from '@/domains/auth/types/api/requests/SignInRequest';
import type { UpdatePasswordRequest } from '@/domains/auth/types/api/requests/UpdatePasswordRequest';
import type { OtpConfigurationResponse } from '@/domains/auth/types/api/responses/OtpConfigurationResponse';

const initialState: AuthSliceInitialState = {
  otpConfig: null,
  statuses: {
    signInInProgress: false,
    otpConfigIsLoading: false,
    isForgotUsernameInProgress: false,
    isForgotPasswordInProgress: false,
    isUpdatePasswordInProgress: false,
  },
};

const authSlice = createSlice({
  name: 'authSlice',
  initialState,
  reducers: {
    runCycleTokenChecker: () => {},
    logout: () => {},
    refresh: () => {},
    signIn: (state, _action: PayloadAction<SignInRequest>) => {
      state.statuses.signInInProgress = true;
    },
    signInSuccess: (state) => {
      state.statuses.signInInProgress = false;
    },
    getOtpConfigIn: (state) => {
      state.statuses.otpConfigIsLoading = true;
    },
    getOtpConfigInSuccess: (state, { payload }: PayloadAction<OtpConfigurationResponse>) => {
      state.statuses.otpConfigIsLoading = false;
      state.otpConfig = payload;
    },
    otpSignIn: (state, _action: PayloadAction<OtpSignInRequest>) => {
      state.statuses.signInInProgress = true;
    },
    otpSignInSuccess: (state) => {
      state.statuses.signInInProgress = false;
    },
    forgotUsername: (state, _action: PayloadAction<ForgotUsernameRequest>) => {
      state.statuses.isForgotUsernameInProgress = true;
    },
    forgotUsernameSuccess: (state) => {
      state.statuses.isForgotUsernameInProgress = false;
    },
    forgotPassword: (state, _action: PayloadAction<ForgotPasswordRequest>) => {
      state.statuses.isForgotPasswordInProgress = true;
    },
    forgotPasswordSuccess: (state) => {
      state.statuses.isForgotPasswordInProgress = false;
    },
    updatePassword: (state, _action: PayloadAction<UpdatePasswordRequest>) => {
      state.statuses.isUpdatePasswordInProgress = true;
    },
    updatePasswordSuccess: (state) => {
      state.statuses.isUpdatePasswordInProgress = false;
    },
    resetAuthStatuses: (state) => {
      state.statuses = initialState.statuses;
    },
  },
});

export const {
  logout,
  refresh,
  runCycleTokenChecker,
  signIn,
  signInSuccess,
  getOtpConfigIn,
  getOtpConfigInSuccess,
  otpSignIn,
  otpSignInSuccess,
  forgotUsername,
  forgotUsernameSuccess,
  forgotPassword,
  forgotPasswordSuccess,
  updatePassword,
  updatePasswordSuccess,
  resetAuthStatuses,
} = authSlice.actions;

export const authSliceReducer = authSlice.reducer;

export const authSliceReducerPath = authSlice.reducerPath;
